<template>
	<BaseLayout
		:content-full-height="true"
		:footer-full="true"
		:include-padding="false"
		background-color-class="bg-color-white"
	>
		<BasePlaceHolder :line-count="10" :placehold="isLoading" :class="{ 'container marketing-section': isLoading }">
			<!-- Why Fundrise -->
			<WhyFundriseSection :data="whyFundriseSectionData" :secondary-header="false" />
			<!-- Our Technology -->
			<OurTechnologySlice :data="ourTechnologySectionData" :secondary-header="true" />
			<!-- Annual Returns Slice -->
			<AnnualReturnsSlice v-if="returnsSectionData.snapshotEnabled" :data="returnsSectionData" />
			<!-- Our Assets -->
			<OurAssetsSlice :data="assetsInfoCardSection" />
			<!-- Investor First Alignment -->
			<InvestorFirstSlice :data="investorAlignmentData" />
			<!-- Make Your Move -->
			<CtaSection :theme="sanityWhyFundrisePageData?.ctaDarkTheme ? 'dark' : 'light'" />
		</BasePlaceHolder>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { SanityInfoCard, SanityInfoCardsSection } from 'types/sanity/marketing-components';
import {
	WhyFundriseAnnualReturnsData,
	WhyFundriseInvestorAlignmentData,
	WhyFundrisePageData
} from 'types/sanity/why-fundrise';
import AnnualReturnsSlice from '@components/marketing-site/why-fundrise/annual-returns-slice.vue';
import CtaSection from '@components/marketing-site/cta-section.vue';
import { getWhyFundriseSanityPage } from '@api/sanity/graphql/why-fundrise-page';
import InvestorFirstSlice from '@components/marketing-site/why-fundrise/investor-first-slice.vue';
import OurAssetsSlice from '@components/marketing-site/why-fundrise/our-assets-slice.vue';
import OurTechnologySlice from '@components/marketing-site/why-fundrise/our-technology-slice.vue';
import { useHead } from '@unhead/vue';
import WhyFundriseSection from '@components/marketing-site/why-fundrise/why-fundrise-section.vue';
import { WhyFundriseSectionData } from 'types/marketing-site';

const isLoading = ref(true);

const sanityWhyFundrisePageData = ref<WhyFundrisePageData | null>(null);

const whyFundriseSectionData = computed((): WhyFundriseSectionData => {
	return {
		theme: sanityWhyFundrisePageData.value?.whyFundriseIsDarkTheme ? 'dark' : 'light',
		video: {
			diagram: sanityWhyFundrisePageData.value?.whyDiagramVideo?.asset.url ?? '',
			diagramMobile: sanityWhyFundrisePageData.value?.whyDiagramVideoMobile?.asset.url ?? '',
			diagramAnimation: sanityWhyFundrisePageData.value?.whyDiagramAnimation?.asset.url ?? '',
			diagramAnimationMobile: sanityWhyFundrisePageData.value?.whyDiagramAnimationMobile?.asset.url ?? '',
			fallbackImage: sanityWhyFundrisePageData.value?.whyDiagramFallback?.asset.url ?? '',
			fallbackImageMobile: sanityWhyFundrisePageData.value?.whyDiagramFallbackMobile?.asset.url ?? ''
		},
		diagramSubtext: sanityWhyFundrisePageData.value?.whyDiagramSubtext ?? '',
		header: {
			title: sanityWhyFundrisePageData.value?.whyEyebrow ?? '',
			heading: sanityWhyFundrisePageData.value?.whyTitle ?? ''
		},
		cards: sanityWhyFundrisePageData?.value?.whyCards?.map((card) => ({
			title: card.toutHeading,
			description: card.toutSummary,
			cta: {
				text: card.toutCta?.linkText,
				href: card.toutCta?.url,
				target: card.toutCta?.openInNewTab ? '_blank' : '_self'
			}
		}))
	};
});

const returnsSectionData = computed((): WhyFundriseAnnualReturnsData => {
	return {
		snapshotEnabled: sanityWhyFundrisePageData.value?.annualReturnsSnapshotEnabled ?? false,
		heading: sanityWhyFundrisePageData.value?.snapshotHeading ?? '',
		title: sanityWhyFundrisePageData.value?.snapshotTitle ?? '',
		description: sanityWhyFundrisePageData.value?.snapshotDescription ?? [],
		tableHeading: sanityWhyFundrisePageData.value?.snapshotTableHeading ?? '',
		linkText: sanityWhyFundrisePageData.value?.snapshotLinkText ?? '',
		linkUrl: sanityWhyFundrisePageData.value?.snapshotLinkUrl ?? ''
	};
});

const assetsInfoCardSection = computed((): SanityInfoCard => {
	return {
		image: sanityWhyFundrisePageData.value?.assetsImage,
		heading: sanityWhyFundrisePageData.value?.assetsHeading ?? '',
		title: sanityWhyFundrisePageData.value?.assetsTitle ?? '',
		description: sanityWhyFundrisePageData.value?.assetsDescription ?? '',
		link: {
			url: sanityWhyFundrisePageData.value?.assetsLinkUrl ?? null,
			linkText: sanityWhyFundrisePageData.value?.assetsLinkText ?? '',
			openInNewTab: true
		}
	};
});

const investorAlignmentData = computed((): WhyFundriseInvestorAlignmentData => {
	return {
		isDarkTheme: sanityWhyFundrisePageData.value?.alignmentDarkTheme ?? false,
		panelOverline: sanityWhyFundrisePageData.value?.alignmentEyebrow ?? '',
		panelHeading: sanityWhyFundrisePageData.value?.alignmentTitle ?? '',
		panelSummary: sanityWhyFundrisePageData.value?.alignmentDescription ?? '',
		panelTouts:
			sanityWhyFundrisePageData.value?.alignmentCards?.map((card) => ({
				toutHeading: card.toutHeading,
				toutSummary: card.toutSummary,
				toutCta:
					card.toutCta?.linkText && card.toutCta?.url
						? {
								linkText: card.toutCta?.linkText,
								url: card.toutCta?.url,
								openInNewTab: !!card.toutCta?.openInNewTab
							}
						: undefined
			})) ?? []
	};
});

const ourTechnologySectionData = computed((): SanityInfoCardsSection => {
	const techData = sanityWhyFundrisePageData.value?.ourTechnologySection;

	return {
		panelHeading: techData?.panelHeading ?? '',
		panelOverline: techData?.panelOverline ?? '',
		sectionSummary: techData?.sectionSummary ?? '',
		sectionCards: techData?.sectionCards ?? [],
		isSectionDarkTheme: techData?.isSectionDarkTheme ?? false
	};
});

async function setup() {
	useHead({
		title: 'Why Fundrise',
		meta: [
			{ property: 'og:title', content: 'Why Fundrise' },
			{
				name: 'description',
				content: 'We built the first fully integrated, end-to-end technology platform for alternative assets.'
			},
			{
				property: 'og:description',
				content: 'We built the first fully integrated, end-to-end technology platform for alternative assets.'
			}
		]
	});
	try {
		sanityWhyFundrisePageData.value = await getWhyFundriseSanityPage();
	} catch (e) {
	} finally {
		isLoading.value = false;
	}
}
setup();
</script>
